import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Coins, Package, Pencil, Plus, Puzzle, Trash2 } from 'lucide-react';
import PlatformSettingsLayout from '@/components/settings/platform/PlatformSettingsLayoutV2';
import TopupPackageDialog from '@/components/settings/platform/v2/TopupPackageDialogV2';
import TopupRangeDialog from '@/components/settings/platform/v2/TopupRangeDialogV2';
import AddonDialog from '@/components/settings/platform/v2/AddonDialogV2';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { ListCard, ListRow, ListRowSkeleton } from '@ampm-7en-ai/ui/components/ListRow';
import { EmptyState } from '@ampm-7en-ai/ui/components/EmptyState';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Pill } from '@ampm-7en-ai/ui/components/StatusBadge';
import { SegmentedControl, type SegmentedOption } from '@ampm-7en-ai/ui/components/SegmentedControl';
import { Field, Input } from '@ampm-7en-ai/ui/components/Input';
import { Select } from '@ampm-7en-ai/ui/components/Select';
import { Textarea } from '@ampm-7en-ai/ui/components/Textarea';
import { ToggleRow } from '@ampm-7en-ai/ui/components/ToggleRow';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { ConfirmDialog } from '@ampm-7en-ai/ui/components/ConfirmDialog';
import { useToast } from '@/hooks/use-toast';
import { price } from '@/lib/money';
import { BASE_URL, getAuthHeaders } from '@/utils/api-config';
import { useSubscription, type SubscriptionPlan } from '@/hooks/useSubscription';
import { useBillingConfig, useUpdateBillingConfig } from '@/hooks/useBillingConfig';
import { useTopupPackages, useDeleteTopupPackage, type TopupPackage } from '@/hooks/useTopupPackages';
import { useTopupRanges, useDeleteTopupRange, type TopupRange } from '@/hooks/useTopupRanges';
import { useAddons, useDeleteAddon, type Addon } from '@/hooks/useAddons';

/**
 * What the platform charges for, and how it invoices.
 *
 * Four things live here — plans, add-ons, top-ups and the invoicing rules —
 * so they are one choice at a time rather than a single scroll. Everything
 * destructive asks first, and names what it is about to remove.
 */

type Tab = 'plans' | 'addons' | 'topups' | 'invoicing';

const TABS: SegmentedOption<Tab>[] = [
  { value: 'plans', label: 'Plans' },
  { value: 'addons', label: 'Add-ons' },
  { value: 'topups', label: 'Top-ups' },
  { value: 'invoicing', label: 'Invoicing' },
];

const CURRENCIES = [
  { value: 'usd', label: 'USD' },
  { value: 'eur', label: 'EUR' },
  { value: 'gbp', label: 'GBP' },
  { value: 'chf', label: 'CHF' },
];

/** The rows of a list that hasn't arrived yet. */
function Rows({ count = 3 }: { count?: number }) {
  return (
    <ListCard>
      {Array.from({ length: count }, (_, index) => (
        <li key={index}>
          <ListRowSkeleton />
        </li>
      ))}
    </ListCard>
  );
}

export default function BillingSettingsV2() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = React.useState<Tab>('plans');

  const { subscriptionPlans, isLoadingSubscriptionPlans, refetchSubscriptionPlans } = useSubscription({
    fetchCurrent: false,
    fetchAllPlans: true,
    fetchInvoice: false,
  });
  const { data: addons, isLoading: loadingAddons } = useAddons();
  const { data: packages, isLoading: loadingPackages } = useTopupPackages();
  const { data: ranges, isLoading: loadingRanges } = useTopupRanges();
  const { data: config, isLoading: loadingConfig } = useBillingConfig();

  const deletePackage = useDeleteTopupPackage();
  const deleteRange = useDeleteTopupRange();
  const deleteAddon = useDeleteAddon();
  const updateConfig = useUpdateBillingConfig();

  /* ---------------------------------------------------------------- *
   * Dialogs
   * ---------------------------------------------------------------- */

  const [planToDelete, setPlanToDelete] = React.useState<SubscriptionPlan | undefined>();
  const [deletingPlan, setDeletingPlan] = React.useState(false);
  const [packageDialog, setPackageDialog] = React.useState<{ open: boolean; editing: TopupPackage | null }>({
    open: false,
    editing: null,
  });
  const [rangeDialog, setRangeDialog] = React.useState<{ open: boolean; editing: TopupRange | null }>({
    open: false,
    editing: null,
  });
  const [addonDialog, setAddonDialog] = React.useState<{ open: boolean; editing: Addon | null }>({
    open: false,
    editing: null,
  });
  const [removing, setRemoving] = React.useState<
    { kind: 'package' | 'range' | 'addon'; id: number; name: string } | null
  >(null);

  const confirmRemoval = async () => {
    if (!removing) return;
    try {
      if (removing.kind === 'package') await deletePackage.mutateAsync(removing.id);
      if (removing.kind === 'range') await deleteRange.mutateAsync(removing.id);
      if (removing.kind === 'addon') await deleteAddon.mutateAsync(removing.id);
      toast({ title: `${removing.name} deleted` });
    } catch (error) {
      toast({
        title: 'That didn’t work',
        description: error instanceof Error ? error.message : 'It wasn’t deleted.',
        variant: 'destructive',
      });
    } finally {
      setRemoving(null);
    }
  };

  const deletePlan = async () => {
    if (!planToDelete?.id) return;
    setDeletingPlan(true);
    try {
      const stored = localStorage.getItem('user');
      const token = stored ? JSON.parse(stored).accessToken : null;
      if (!token) throw new Error('You need to be signed in to delete a plan.');

      const response = await fetch(`${BASE_URL}subscriptions/delete/${planToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      });
      if (!response.ok) throw new Error('The plan wasn’t deleted.');

      toast({ title: `${planToDelete.name} deleted` });
      setPlanToDelete(undefined);
      refetchSubscriptionPlans?.();
    } catch (error) {
      toast({
        title: 'That didn’t work',
        description: error instanceof Error ? error.message : 'The plan wasn’t deleted.',
        variant: 'destructive',
      });
    } finally {
      setDeletingPlan(false);
    }
  };

  /* ---------------------------------------------------------------- *
   * Invoicing rules
   * ---------------------------------------------------------------- */

  const [rules, setRules] = React.useState({
    default_currency: 'usd',
    default_tax_rate: '10',
    enable_auto_renewal: true,
    enable_proration: true,
    auto_send_receipts: true,
    company_name: '',
    company_address: '',
    invoice_footer_text: '',
    payment_reminder_days: '3',
    enable_overdue_notifications: true,
  });

  React.useEffect(() => {
    if (!config) return;
    setRules({
      default_currency: (config.default_currency || 'usd').toLowerCase(),
      default_tax_rate: config.default_tax_rate ?? '0',
      enable_auto_renewal: config.enable_auto_renewal,
      enable_proration: config.enable_proration,
      auto_send_receipts: config.auto_send_receipts,
      company_name: config.company_name ?? '',
      company_address: config.company_address ?? '',
      invoice_footer_text: config.invoice_footer_text ?? '',
      payment_reminder_days: String(config.payment_reminder_days ?? 3),
      enable_overdue_notifications: config.enable_overdue_notifications,
    });
  }, [config]);

  const setRule = (change: Partial<typeof rules>) => setRules((current) => ({ ...current, ...change }));

  const saveRules = async () => {
    try {
      await updateConfig.mutateAsync({
        default_currency: rules.default_currency.toUpperCase(),
        default_tax_rate: Number.parseFloat(rules.default_tax_rate) || 0,
        enable_auto_renewal: rules.enable_auto_renewal,
        enable_proration: rules.enable_proration,
        auto_send_receipts: rules.auto_send_receipts,
        company_name: rules.company_name,
        company_address: rules.company_address,
        invoice_footer_text: rules.invoice_footer_text,
        payment_reminder_days: Number.parseInt(rules.payment_reminder_days, 10) || 0,
        enable_overdue_notifications: rules.enable_overdue_notifications,
      });
      toast({ title: 'Billing settings saved' });
    } catch (error) {
      toast({
        title: 'That didn’t save',
        description: error instanceof Error ? error.message : 'The billing settings weren’t saved.',
        variant: 'destructive',
      });
    }
  };

  return (
    <PlatformSettingsLayout title="Billing" description="What the platform charges for, and how it invoices.">
      <div className="mb-5">
        <SegmentedControl size="sm" aria-label="Billing section" options={TABS} value={tab} onValueChange={setTab} />
      </div>

      {/* ---------------- Plans ---------------- */}
      {tab === 'plans' && (
        <>
          <div className="mb-3 flex items-end justify-between gap-3">
            <p className="text-ui-sm text-ui-fg-muted">What a workspace subscribes to, and what it gets.</p>
            <Button variant="primary" onClick={() => navigate('/v2/settings/platform/subscription-plan')}>
              <Plus /> New plan
            </Button>
          </div>

          {isLoadingSubscriptionPlans ? (
            <Rows />
          ) : (subscriptionPlans ?? []).length === 0 ? (
            <Panel variant="dashed">
              <EmptyState icon={Package} title="No plans yet" description="A plan is what a workspace pays for each month." />
            </Panel>
          ) : (
            <ListCard>
              {(subscriptionPlans ?? []).map((plan) => (
                <li key={plan.id}>
                  <ListRow
                    title={<span className="truncate">{plan.name}</span>}
                    description={
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {plan.description && <span className="text-ui-xs text-ui-fg-subtle">{plan.description}</span>}
                        {plan.total_replies != null && (
                          <span className="text-ui-xs text-ui-fg-subtle">
                            {plan.total_replies.toLocaleString()} replies
                          </span>
                        )}
                      </span>
                    }
                    meta={
                      <span className="flex items-center gap-2">
                        <span className="text-ui-sm text-ui-fg">
                          {plan.is_free ? 'Free' : price(plan.price_monthly ?? plan.price, '/mo')}
                        </span>
                        {plan.status && <Pill tone={plan.status === 'ACTIVE' ? 'success' : 'neutral'}>{plan.status}</Pill>}
                      </span>
                    }
                    onClick={() => navigate(`/v2/settings/platform/subscription-plan/${plan.id}`)}
                    trailing={
                      <Button
                        variant="ghost"
                        aria-label={`Delete ${plan.name}`}
                        className="text-ui-danger"
                        onClick={(event) => {
                          event.stopPropagation();
                          setPlanToDelete(plan);
                        }}
                      >
                        <Trash2 />
                      </Button>
                    }
                  />
                </li>
              ))}
            </ListCard>
          )}
        </>
      )}

      {/* ---------------- Add-ons ---------------- */}
      {tab === 'addons' && (
        <>
          <div className="mb-3 flex items-end justify-between gap-3">
            <p className="text-ui-sm text-ui-fg-muted">Extras a workspace can buy on top of its plan.</p>
            <Button variant="primary" onClick={() => setAddonDialog({ open: true, editing: null })}>
              <Plus /> New add-on
            </Button>
          </div>

          {loadingAddons ? (
            <Rows />
          ) : (addons ?? []).length === 0 ? (
            <Panel variant="dashed">
              <EmptyState icon={Puzzle} title="No add-ons yet" description="An add-on is billed monthly beside the plan." />
            </Panel>
          ) : (
            <ListCard>
              {(addons ?? []).map((addon) => (
                <li key={addon.id}>
                  <ListRow
                    title={<span className="truncate">{addon.name}</span>}
                    description={
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {addon.description && <span className="text-ui-xs text-ui-fg-subtle">{addon.description}</span>}
                        <span className="font-ui-mono text-ui-xs text-ui-fg-subtle">{addon.addon_type}</span>
                      </span>
                    }
                    meta={
                      <span className="flex items-center gap-2">
                        <span className="text-ui-sm text-ui-fg">{price(addon.price_monthly, '/mo')}</span>
                        <Pill tone={addon.status === 'ACTIVE' ? 'success' : 'neutral'}>{addon.status}</Pill>
                      </span>
                    }
                    onClick={() => setAddonDialog({ open: true, editing: addon })}
                    trailing={
                      <Button
                        variant="ghost"
                        aria-label={`Delete ${addon.name}`}
                        className="text-ui-danger"
                        onClick={(event) => {
                          event.stopPropagation();
                          setRemoving({ kind: 'addon', id: addon.id, name: addon.name });
                        }}
                      >
                        <Trash2 />
                      </Button>
                    }
                  />
                </li>
              ))}
            </ListCard>
          )}
        </>
      )}

      {/* ---------------- Top-ups ---------------- */}
      {tab === 'topups' && (
        <div className="space-y-8">
          <section>
            <div className="mb-3 flex items-end justify-between gap-3">
              <div>
                <h2 className="text-ui-lg font-normal text-ui-fg">Packages</h2>
                <p className="mt-0.5 text-ui-sm text-ui-fg-muted">A fixed number of replies for a fixed price.</p>
              </div>
              <Button variant="secondary" onClick={() => setPackageDialog({ open: true, editing: null })}>
                <Plus /> New package
              </Button>
            </div>

            {loadingPackages ? (
              <Rows count={2} />
            ) : (packages ?? []).length === 0 ? (
              <Panel variant="dashed">
                <EmptyState icon={Coins} title="No packages yet" description="A package sells replies in a set bundle." />
              </Panel>
            ) : (
              <ListCard>
                {(packages ?? []).map((pkg) => (
                  <li key={pkg.id}>
                    <ListRow
                      title={<span className="truncate">{pkg.name}</span>}
                      description={<span className="text-ui-xs text-ui-fg-subtle">{pkg.replies.toLocaleString()} replies</span>}
                      meta={
                        <span className="flex items-center gap-2">
                          <span className="text-ui-sm text-ui-fg">{price(pkg.amount)}</span>
                          <Pill tone={pkg.status === 'ACTIVE' ? 'success' : 'neutral'}>{pkg.status}</Pill>
                        </span>
                      }
                      onClick={() => setPackageDialog({ open: true, editing: pkg })}
                      trailing={
                        <Button
                          variant="ghost"
                          aria-label={`Delete ${pkg.name}`}
                          className="text-ui-danger"
                          onClick={(event) => {
                            event.stopPropagation();
                            setRemoving({ kind: 'package', id: pkg.id, name: pkg.name });
                          }}
                        >
                          <Trash2 />
                        </Button>
                      }
                    />
                  </li>
                ))}
              </ListCard>
            )}
          </section>

          <section>
            <div className="mb-3 flex items-end justify-between gap-3">
              <div>
                <h2 className="text-ui-lg font-normal text-ui-fg">Ranges</h2>
                <p className="mt-0.5 text-ui-sm text-ui-fg-muted">
                  Pay per reply, at a rate that changes with how many are bought.
                </p>
              </div>
              <Button variant="secondary" onClick={() => setRangeDialog({ open: true, editing: null })}>
                <Plus /> New range
              </Button>
            </div>

            {loadingRanges ? (
              <Rows count={2} />
            ) : (ranges ?? []).length === 0 ? (
              <Panel variant="dashed">
                <EmptyState icon={Coins} title="No ranges yet" description="A range prices replies by the quantity bought." />
              </Panel>
            ) : (
              <ListCard>
                {(ranges ?? []).map((range) => (
                  <li key={range.id}>
                    <ListRow
                      title={<span className="truncate">{range.name}</span>}
                      description={
                        <span className="text-ui-xs text-ui-fg-subtle">
                          {range.min_qty.toLocaleString()}–{range.max_qty.toLocaleString()} replies
                        </span>
                      }
                      meta={
                        <span className="flex items-center gap-2">
                          <span className="text-ui-sm text-ui-fg">{price(range.price_per_reply)} each</span>
                          <Pill tone={range.active ? 'success' : 'neutral'}>{range.active ? 'Active' : 'Inactive'}</Pill>
                        </span>
                      }
                      onClick={() => setRangeDialog({ open: true, editing: range })}
                      trailing={
                        <Button
                          variant="ghost"
                          aria-label={`Delete ${range.name}`}
                          className="text-ui-danger"
                          onClick={(event) => {
                            event.stopPropagation();
                            setRemoving({ kind: 'range', id: range.id, name: range.name });
                          }}
                        >
                          <Trash2 />
                        </Button>
                      }
                    />
                  </li>
                ))}
              </ListCard>
            )}
          </section>
        </div>
      )}

      {/* ---------------- Invoicing ---------------- */}
      {tab === 'invoicing' && (
        <div className="space-y-4">
          <Panel padding="md">
            <h2 className="text-ui-base font-medium text-ui-fg">Charging</h2>
            <p className="mt-0.5 text-ui-xs text-ui-fg-muted">How the platform bills, and in what currency.</p>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="Currency" htmlFor="currency">
                <Select
                  id="currency"
                  options={CURRENCIES}
                  value={rules.default_currency}
                  onValueChange={(value) => setRule({ default_currency: value })}
                  disabled={loadingConfig}
                />
              </Field>
              <Field label="Tax rate" htmlFor="tax-rate" hint="A percentage, applied to every invoice.">
                <Input
                  id="tax-rate"
                  type="number"
                  min={0}
                  step="0.01"
                  value={rules.default_tax_rate}
                  onChange={(event) => setRule({ default_tax_rate: event.target.value })}
                  disabled={loadingConfig}
                />
              </Field>
            </div>

            <div className="mt-4 space-y-2">
              <ToggleRow
                label="Renew automatically"
                description="Plans carry on at the end of a period instead of lapsing."
                checked={rules.enable_auto_renewal}
                onCheckedChange={(enable_auto_renewal) => setRule({ enable_auto_renewal })}
                disabled={loadingConfig}
              />
              <ToggleRow
                label="Prorate changes"
                description="Mid-period upgrades are charged for the part of the period they cover."
                checked={rules.enable_proration}
                onCheckedChange={(enable_proration) => setRule({ enable_proration })}
                disabled={loadingConfig}
              />
            </div>
          </Panel>

          <Panel padding="md">
            <h2 className="text-ui-base font-medium text-ui-fg">On the invoice</h2>
            <p className="mt-0.5 text-ui-xs text-ui-fg-muted">What customers see at the top and bottom of every one.</p>

            <div className="mt-5 space-y-4">
              <Field label="Company name" htmlFor="company-name">
                <Input
                  id="company-name"
                  value={rules.company_name}
                  onChange={(event) => setRule({ company_name: event.target.value })}
                  disabled={loadingConfig}
                />
              </Field>
              <Field label="Address" htmlFor="company-address">
                <Textarea
                  id="company-address"
                  rows={2}
                  value={rules.company_address}
                  onChange={(event) => setRule({ company_address: event.target.value })}
                  disabled={loadingConfig}
                />
              </Field>
              <Field label="Footer" htmlFor="invoice-footer" hint="The last line on every invoice.">
                <Textarea
                  id="invoice-footer"
                  rows={2}
                  value={rules.invoice_footer_text}
                  onChange={(event) => setRule({ invoice_footer_text: event.target.value })}
                  disabled={loadingConfig}
                />
              </Field>
            </div>
          </Panel>

          <Panel padding="md">
            <h2 className="text-ui-base font-medium text-ui-fg">Chasing payment</h2>
            <p className="mt-0.5 text-ui-xs text-ui-fg-muted">When to remind, and whether to keep going.</p>

            <Field
              label="Remind this many days before"
              htmlFor="reminder-days"
              className="mt-5 max-w-xs"
              hint="How long before a payment is due the first reminder goes out."
            >
              <Input
                id="reminder-days"
                type="number"
                min={0}
                value={rules.payment_reminder_days}
                onChange={(event) => setRule({ payment_reminder_days: event.target.value })}
                disabled={loadingConfig}
              />
            </Field>

            <div className="mt-4 space-y-2">
              <ToggleRow
                label="Send receipts"
                description="Email a receipt as soon as a payment goes through."
                checked={rules.auto_send_receipts}
                onCheckedChange={(auto_send_receipts) => setRule({ auto_send_receipts })}
                disabled={loadingConfig}
              />
              <ToggleRow
                label="Chase overdue invoices"
                description="Keep emailing after the due date has passed."
                checked={rules.enable_overdue_notifications}
                onCheckedChange={(enable_overdue_notifications) => setRule({ enable_overdue_notifications })}
                disabled={loadingConfig}
              />
            </div>
          </Panel>

          <div className="flex justify-end">
            <Button variant="primary" onClick={() => void saveRules()} disabled={updateConfig.isPending || loadingConfig}>
              {updateConfig.isPending ? 'Saving…' : 'Save billing settings'}
            </Button>
          </div>
        </div>
      )}

      {/* ---------------- Dialogs ---------------- */}
      <ConfirmDialog
        open={planToDelete !== undefined}
        onOpenChange={(open) => !open && !deletingPlan && setPlanToDelete(undefined)}
        title={`Delete ${planToDelete?.name ?? 'this plan'}?`}
        description="Workspaces already on it keep it until their period ends; nobody new can subscribe. This can’t be undone."
        confirmLabel={deletingPlan ? 'Deleting…' : 'Delete plan'}
        tone="danger"
        onConfirm={deletePlan}
      />

      <TopupPackageDialog
        open={packageDialog.open}
        onOpenChange={(open) => setPackageDialog({ open, editing: open ? packageDialog.editing : null })}
        editPackage={packageDialog.editing}
      />

      <TopupRangeDialog
        open={rangeDialog.open}
        onOpenChange={(open) => setRangeDialog({ open, editing: open ? rangeDialog.editing : null })}
        editRange={rangeDialog.editing}
      />

      <AddonDialog
        open={addonDialog.open}
        onOpenChange={(open) => setAddonDialog({ open, editing: open ? addonDialog.editing : null })}
        addon={addonDialog.editing}
      />

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={`Delete ${removing?.name ?? 'this'}?`}
        description="Workspaces already on it keep what they bought; nobody can buy it again. This can’t be undone."
        confirmLabel="Delete"
        tone="danger"
        onConfirm={confirmRemoval}
      />
    </PlatformSettingsLayout>
  );
}
